import { useId } from "react";
import type { EntryKind } from "../api";
import { ENTRY_KIND_LABELS } from "../api";
import { cx } from "../components";
import styles from "./HistoriqueFiltres.module.css";

/** Nature retenue par le filtre, ou `"toutes"` — c'est-à-dire aucun filtre. */
export type NatureFiltre = EntryKind | "toutes";

/**
 * Ordre d'affichage : celui du cycle de vie d'un token, pas l'ordre
 * alphabétique. On achète, on réserve, on consomme ; on libère ou on expire ;
 * on ajuste en dernier parce que c'est l'exception.
 */
const NATURES: EntryKind[] = [
  "purchase",
  "reserve",
  "consume",
  "release",
  "expire",
  "adjust",
];

export type HistoriqueFiltresProps = {
  /** Nature sélectionnée. */
  valeur: NatureFiltre;
  onChange: (valeur: NatureFiltre) => void;
  /**
   * Nombre d'écritures **déjà chargées** pour chaque nature. Affiché sur
   * chaque puce : un filtre qui annonce son propre résultat évite de cliquer
   * pour découvrir qu'il ne rend rien.
   */
  comptes: Record<NatureFiltre, number>;
  /**
   * Vrai quand le journal entier a été chargé. Change le texte de portée : le
   * même filtre est partiel tant qu'il reste des pages, exhaustif ensuite.
   */
  complet: boolean;
};

/**
 * Filtre par nature de mouvement.
 *
 * **Le filtre est appliqué côté client, sur les seules écritures chargées** :
 * l'API `/entries` ne connaît pas de paramètre de nature, et en fabriquer un
 * en tirant toutes les pages pour n'en garder qu'une poignée transformerait un
 * clic en téléchargement du grand livre entier.
 *
 * C'est un compromis, et il est dit à l'utilisateur en toutes lettres sous les
 * puces : un filtre qui a l'air exhaustif sans l'être fait conclure « il n'y a
 * aucun achat » là où il fallait lire « aucun achat parmi les 50 dernières
 * écritures ».
 *
 * Des boutons radio et non des boutons : ils sont exclusifs par nature, se
 * parcourent aux flèches du clavier, et le `fieldset`/`legend` annonce le
 * groupe avant de lire les options.
 */
export function HistoriqueFiltres({
  valeur,
  onChange,
  comptes,
  complet,
}: HistoriqueFiltresProps) {
  // Un nom unique par instance : deux groupes de radios partageant un `name`
  // se désélectionneraient l'un l'autre.
  const nom = useId();

  const options: { cle: NatureFiltre; libelle: string }[] = [
    { cle: "toutes", libelle: "Toutes" },
    ...NATURES.map((nature) => ({
      cle: nature as NatureFiltre,
      libelle: ENTRY_KIND_LABELS[nature],
    })),
  ];

  return (
    <fieldset className={styles.groupe}>
      <legend className={styles.legende}>Nature du mouvement</legend>

      <div className={styles.choix}>
        {options.map((option) => (
          <label
            key={option.cle}
            className={cx(styles.puce, valeur === option.cle && styles.actif)}
          >
            <input
              type="radio"
              name={nom}
              value={option.cle}
              checked={valeur === option.cle}
              onChange={() => onChange(option.cle)}
              // Masqué à l'œil mais bien présent : c'est lui qui porte le
              // focus, la sélection et la navigation aux flèches. Un `div`
              // avec `onClick` aurait l'air identique et serait inutilisable
              // au clavier.
              className="lde-visually-hidden"
            />
            <span>{option.libelle}</span>
            <span className={styles.compte}>{comptes[option.cle]}</span>
          </label>
        ))}
      </div>

      <p className={styles.portee}>
        {complet
          ? "Tout le journal est chargé : ce filtre porte donc sur l'intégralité des écritures du portefeuille."
          : "Ce filtre porte sur les écritures déjà chargées, pas sur tout le journal : l'API ne sait pas filtrer par nature. Chargez la suite pour élargir la recherche."}
      </p>
    </fieldset>
  );
}
